import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Brush,
} from 'recharts'
import KpiCard from '../../components/KpiCard'
import { Widget } from '../../components/dashboard/Widget'
import { DemandsAttentionSection, type AttentionItem } from '../../components/dashboard/DemandsAttentionSection'
import AlertsFeed from '../../components/AlertsFeed'
import { TopologyView } from '../ScadaView'
import { AlertHistorySection } from '../ReportsPage'
import { useDeviceDetailModal } from '../../hooks/useDeviceDetailModal'
import {
  scadaApi, type DeviceCommErrorCategory, type MasterConnErrorCategory,
  type PumpStatus, type ReservoirStatus,
} from '../../api/scada'
import { edgeConnStatus } from '../../utils/edgeStatus'
import { useAuthStore } from '../../store/authStore'
import { pumpPulse, reservoirPulse } from '../../utils/pulseCategory'

// Distinct from staleness (status 'no_signal' from the backend) -- ScadaView.tsx's PumpCard/ReservoirCard treat "never reported at
// all" as its own "unknown" state (pump.active === null / res.depth_m ===
// null), separate from "went stale after reporting", and show it as
// "NEPOZNATO" / "Nema podataka sa senzora". Used here only for detail text
// (which specific message to show), not for list inclusion anymore -- that's
// the shared classifier's job now.
function deviceHasNoData(d: PumpStatus | ReservoirStatus): boolean {
  return 'active' in d ? d.active === null : d.depth_m === null
}

const COMM_ERROR_LABEL: Partial<Record<DeviceCommErrorCategory, string>> = {
  IllegalAddress: 'Nepostojeća adresa',
  no_response:    'Bez odgovora',
}
function commErrorLabel(category: DeviceCommErrorCategory): string {
  return COMM_ERROR_LABEL[category] ?? `Greška: ${category}`
}

const MASTER_CONN_ERROR_LABEL: Record<MasterConnErrorCategory, string> = {
  unreachable: 'Nedostupno',
  refused:     'Odbijeno',
  reset:       'Prekinuto',
  unknown:     'Nepoznata greška',
}

function ProductionGraphWidget() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['scada-production', 30],
    queryFn: () => scadaApi.production(30).then((r) => r.data),
    retry: false,
  })

  return (
    <Widget>
      <h2 className="text-base font-semibold text-gray-900">Proizvodnja</h2>
      <p className="text-sm text-gray-400 mt-1 mb-4">Ukupna dnevna proizvodnja (m³) — poslednjih 30 dana</p>
      {isLoading ? (
        <div className="h-64 flex items-center justify-center text-gray-400 text-sm">Učitavanje…</div>
      ) : isError || !data || data.length === 0 ? (
        // GET /scada/production is built (scada-to-web-2026-09-02-
        // production.md) but its branch isn't merged yet -- 404s until
        // then, degrading to the same dashed "Uskoro" convention as
        // ScadaZoneReadoutsWidget rather than surfacing that as an error.
        <div className="border-2 border-dashed border-gray-200 rounded-xl h-40 flex flex-col items-center justify-center gap-2">
          <svg className="w-8 h-8 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <p className="text-sm text-gray-400">Uskoro — dnevni pregled proizvodnje</p>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip formatter={(v: number) => [`${v.toLocaleString()} m³`, 'Proizvodnja']} />
            <Line type="monotone" dataKey="volume_m3" name="Proizvodnja" stroke="#37C8FC" strokeWidth={2} dot={false} />
            <Brush dataKey="date" height={24} stroke="#37C8FC" travellerWidth={8} />
          </LineChart>
        </ResponsiveContainer>
      )}
    </Widget>
  )
}

export default function InfrastructureFocus() {
  const { user } = useAuthStore()
  const hasRepair = user?.modules?.includes('repair') ?? false

  const { data: latest } = useQuery({
    queryKey: ['scada-latest'],
    queryFn: () => scadaApi.latest().then((r) => r.data),
    refetchInterval: 30_000,
  })
  const pumps = latest?.pumps ?? []
  const reservoirs = latest?.reservoirs ?? []
  const allDevices = [
    ...pumps.map((p) => ({ ...p, nodeType: 'pump' as const })),
    ...reservoirs.map((r) => ({ ...r, nodeType: 'reservoir' as const })),
  ]

  const { data: zones = [] } = useQuery({
    queryKey: ['scada-zones'],
    queryFn: () => scadaApi.listZones().then((r) => r.data),
    staleTime: 60_000,
  })

  // GET /setup/edge-nodes is company_admin/admin-only server-side today —
  // an executive-only account may 403 here until that's routed separately
  // (same caveat ExecutiveDashboard.tsx's own EdgeServersWidget already
  // flags), building against it now rather than blocking on it.
  const { data: edgeNodes = [] } = useQuery({
    queryKey: ['scada-edge-nodes'],
    queryFn: () => scadaApi.listEdgeNodes().then((r) => r.data),
    refetchInterval: 30_000,
  })

  // Inclusion in "needs attention" now comes from the exact same classifier
  // that colors the tile (and the map's pulse) -- confirmed with the user
  // this should catch genuine live problems (a reservoir draining fast, a
  // pump outside its flow band), not just connectivity/no-data/staleness
  // the way this list worked before. Untestable in this sandbox (no live
  // data flowing to actually trigger a 'problem' case) -- worth a real
  // check once there's real data to watch it against.
  const devicesWithPulse = allDevices.map((d) => ({
    ...d,
    pulse: d.nodeType === 'pump'
      ? pumpPulse(d)
      : reservoirPulse(d),
  }))
  const devicesNeedingAttention = devicesWithPulse.filter((d) => d.pulse.category !== 'positive')
  const edgeNodesNeedingAttention = edgeNodes.filter((n) => n.master_conn_error != null)

  // A faulted pump belongs in the list even while it reads positive (running,
  // severity ok) -- the fault is the reason to go look. The KPI above keeps
  // counting on category alone, so a reporting pump is still "on the network".
  const attentionDevices = devicesWithPulse.filter((d) => d.pulse.category !== 'positive' || d.fault === true)

  const attentionItems: AttentionItem[] = [
    ...attentionDevices.map((d) => ({
      id:        `device-${d.device_key}`,
      label:     d.label,
      // Disconnected: same labels as before (comm error / no data / stale).
      // Problem (only reachable now that the filter catches live issues,
      // not yet exercised against real data): the actual reading, matching
      // "text is usually a 3-digit number" -- reuses the same value each
      // node type's own detail card already shows, not a new format.
      detail: d.pulse.category === 'disconnected'
        ? (d.comm_error ? commErrorLabel(d.comm_error) : deviceHasNoData(d) ? 'Nema podataka' : 'Neaktivan')
        : d.nodeType === 'reservoir'
          ? (d.depth_m != null ? `${d.depth_m.toFixed(1)}m` : '—')
          : (d.flow_lps != null ? `${d.flow_lps.toFixed(0)}` : (d.active ? 'ON' : 'OFF')),
      nodeType:  d.nodeType,
      deviceKey: d.device_key,
      category:  d.pulse.category,
      severity:  d.pulse.severity,
      fault:      d.fault === true,
      faultSince: d.fault_since ?? null,
    })),
    ...edgeNodesNeedingAttention.map((n) => ({
      id:       `edge-${n.id}`,
      label:    n.name,
      detail:   n.master_conn_error ? MASTER_CONN_ERROR_LABEL[n.master_conn_error] : 'Nepoznata greška',
      nodeType: 'edge' as const,
      // An unreachable edge box is "we can't communicate with it" -- the
      // same concept as a device's comm_error/no-data, i.e. disconnected,
      // not a live "problem" (there's no live reading to be wrong here).
      category: 'disconnected' as const,
    })),
  ]

  const { openDeviceDetail, deviceDetailModal } = useDeviceDetailModal()

  const edgeOnlineCount = edgeNodes.filter((n) => edgeConnStatus(n) === 'ok').length
  const activeZoneCount = zones.filter((z) => z.active).length

  const [mapFullscreen, setMapFullscreen] = useState(false)

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiCard
          title="Uređaji na mreži"
          value={`${allDevices.length - devicesNeedingAttention.length}/${allDevices.length}`}
          trend="stable" trendPercent={0} icon="sensors"
        />
        <KpiCard
          title="Zahteva pažnju"
          value={String(attentionItems.length)}
          trend="stable" trendPercent={0} icon="warning"
        />
        <KpiCard
          title="Zone"
          value={String(activeZoneCount)}
          trend="stable" trendPercent={0} icon="layers"
        />
        <KpiCard
          title="Edge serveri"
          value={`${edgeOnlineCount}/${edgeNodes.length}`}
          trend="stable" trendPercent={0} icon="dns"
        />
      </div>

      <DemandsAttentionSection
        title="Zahteva pažnju"
        items={attentionItems}
        onDeviceClick={openDeviceDetail}
      />

      <TopologyView
        pumps={pumps} reservoirs={reservoirs} compact readOnly
        onFullscreen={() => setMapFullscreen(true)}
        onDeviceClick={openDeviceDetail}
      />

      {mapFullscreen && (
        <div className="fixed inset-2 sm:inset-4 z-50 bg-white rounded-2xl shadow-2xl overflow-y-auto">
          <button
            onClick={() => setMapFullscreen(false)}
            className="absolute top-4 right-4 z-10 bg-white rounded-full p-1.5 shadow-md text-gray-500 hover:text-gray-700"
            aria-label="Zatvori"
          >
            <svg className="w-5 h-5" viewBox="0 -960 960 960" fill="currentColor">
              <path d="m256-200-56-56 224-224-224-224 56-56 224 224 224-224 56 56-224 224 224 224-56 56-224-224-224 224Z" />
            </svg>
          </button>
          <TopologyView
            pumps={pumps} reservoirs={reservoirs} readOnly mapHeightClass="h-[75vh]"
            onDeviceClick={openDeviceDetail}
          />
        </div>
      )}

      {hasRepair ? <AlertsFeed /> : <AlertHistorySection />}

      <ProductionGraphWidget />

      {deviceDetailModal}
    </div>
  )
}
